import { AuthenticatedTemplate } from "@azure/msal-react"
import { Box, LinearProgress, Typography } from "@mui/material"
import React, { useCallback, useState } from "react"
import { DiagnosisUploadFile } from "../components/commands";
import PageCard from '../../common/components/styled-ui-elements/PageCard';
import type { UploadProgress } from "../../../services/signalRService";

const PAGE_STYLES = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    padding: 3,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 3,
    borderBottom: '2px solid #3b82f6',
    backgroundColor: '#ffffff',
  },
  content: {
    padding: 3,
    backgroundColor: '#ffffff',
  }
};

// The 202 response never carries loaded/removed counts - they only exist inside the SignalR
// completion event's message. Rendering that message verbatim avoids re-deriving numbers the
// backend already summarized, and keeps one source of truth for the sentence.
//
// 'rejected' and 'failed' are deliberately separate: a rejection happens before the 202, so
// nothing was written; a SignalR failure happens after the write already started, so the catalog
// may be partly updated. Collapsing them into one status would tell an operator "unchanged" when
// it might not be.
type UploadState =
  | { status: 'idle' }
  | { status: 'uploading' }
  | { status: 'completed'; message: string }
  | { status: 'rejected'; message: string }
  | { status: 'failed'; message: string };

function DiagnosisCodes() {
  const [uploadState, setUploadState] = useState<UploadState>({ status: 'idle' });
  const [progressPercentage, setProgressPercentage] = useState(0);

  const handleUploadStart = useCallback(() => {
    setProgressPercentage(0);
    setUploadState({ status: 'uploading' });
  }, []);

  const handleProgress = useCallback((progress: UploadProgress) => {
    setProgressPercentage(progress.progressPercentage);
  }, []);

  const handleCompleted = useCallback((progress: UploadProgress) => {
    setUploadState({ status: 'completed', message: progress.message });
  }, []);

  const handleRejected = useCallback((message: string) => {
    setUploadState({ status: 'rejected', message });
  }, []);

  const handleOperationFailed = useCallback((message: string) => {
    setUploadState({ status: 'failed', message });
  }, []);

  return (
    <AuthenticatedTemplate>
      <Box sx={PAGE_STYLES.container}>
        <PageCard>
          <Box sx={PAGE_STYLES.header}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Diagnosis Codes
            </Typography>
            <DiagnosisUploadFile
              onUploadStart={handleUploadStart}
              onProgress={handleProgress}
              onCompleted={handleCompleted}
              onRejected={handleRejected}
              onOperationFailed={handleOperationFailed}
            />
          </Box>

          <Box sx={PAGE_STYLES.content}>
            {uploadState.status === 'uploading' && (
              <>
                <Typography>Load in progress. Counts will appear when it finishes.</Typography>
                <LinearProgress variant="determinate" value={progressPercentage} />
              </>
            )}
            {uploadState.status === 'completed' && (
              <Typography>{uploadState.message}</Typography>
            )}
            {uploadState.status === 'rejected' && (
              <>
                <Typography color="error">{uploadState.message}</Typography>
                <Typography>The catalog is unchanged.</Typography>
              </>
            )}
            {uploadState.status === 'failed' && (
              <>
                <Typography color="error">{uploadState.message}</Typography>
                <Typography>
                  The load did not finish. The catalog may be partly updated - check it before you
                  rely on it.
                </Typography>
              </>
            )}
          </Box>
        </PageCard>
      </Box>
    </AuthenticatedTemplate>
  )
}

export { DiagnosisCodes }
